/**
 * Load effective channel permissions when entering a channel; refresh when
 * channel/category metadata that affects overrides changes.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { createApiClient, PublicSpaceChannel, PublicSpaceChannelCategory, SpacePermission } from '@adieuu/shared';

type Api = ReturnType<typeof createApiClient>;

export function useActiveChannelPermissions(params: {
  api: Api;
  isLoggedIn: boolean;
  spaceId: string | null | undefined;
  channelId: string | null;
  channels: readonly PublicSpaceChannel[];
  categories: readonly PublicSpaceChannelCategory[];
}) {
  const { api, isLoggedIn, spaceId, channelId, channels, categories } = params;
  const [activeChannelPermissions, setActiveChannelPermissions] = useState<SpacePermission[]>(
    [],
  );
  const [activeChannelPermissionsLoading, setActiveChannelPermissionsLoading] = useState(false);

  const channelStamp = useMemo(() => {
    const ch = channels.find((c) => c.id === channelId);
    if (!ch) return '';
    return `${ch.id}:${ch.updatedAt}:${ch.hasRolePermissionOverrides ? 1 : 0}:${ch.categoryId ?? ''}`;
  }, [channels, channelId]);

  const categoriesStamp = useMemo(
    () =>
      categories
        .map((c) => `${c.id}:${c.updatedAt}:${c.hasRolePermissionOverrides ? 1 : 0}`)
        .join('|'),
    [categories],
  );

  const refresh = useCallback(() => {
    if (!isLoggedIn || !spaceId || !channelId) {
      setActiveChannelPermissions([]);
      setActiveChannelPermissionsLoading(false);
      return;
    }
    let cancelled = false;
    setActiveChannelPermissionsLoading(true);
    void api.spaces.getChannelPermissions(spaceId, channelId).then((res) => {
      if (cancelled) return;
      if (res.success && res.data) {
        setActiveChannelPermissions(res.data.permissions);
      } else {
        setActiveChannelPermissions([]);
      }
      setActiveChannelPermissionsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [api, isLoggedIn, spaceId, channelId]);

  useEffect(() => {
    return refresh();
  }, [refresh, channelStamp, categoriesStamp]);

  const hasActiveChannelPermission = useCallback(
    (permission: SpacePermission) => activeChannelPermissions.includes(permission),
    [activeChannelPermissions],
  );

  return {
    activeChannelPermissions,
    activeChannelPermissionsLoading,
    hasActiveChannelPermission,
    refreshActiveChannelPermissions: refresh,
  };
}
